import React from "react";
import { useTranslation } from "react-i18next";
import "./Products.css";

/*
 * Accueil — Compatibilité multimarques / maquette 05
 * Le nom Products est conservé pour ne pas changer les imports existants.
 * Les noms des constructeurs ne sont ni des liens ni des logos.
 */
const MANUFACTURERS = ["OTIS", "KONE", "SCHINDLER", "TKE", "ORONA", "SODIMAS"];

const CONTENT = {
  fr: {
    label: "Compatibilité multimarques",
    title: "Un parc multimarques. Une seule solution.",
    description:
      "Chaque parc d'ascenseurs est différent. OPENlift est conçu pour s'intégrer à de nombreuses familles de contrôleurs et faciliter le suivi des équipements, quel que soit leur constructeur.",
    note: "Compatibilité selon modèles et configurations",
    groundTitle: "Conçu pour le terrain",
    benefits: [
      "Interopérabilité multi-constructeurs",
      "Intégration progressive des contrôleurs",
      "Supervision centralisée"
    ],
    commonPlatform: "Plateforme commune",
    diagramLabel: "Constructeurs et contrôleurs reliés à une plateforme OPENlift commune"
  },
  en: {
    label: "Multi-brand compatibility",
    title: "A multi-brand fleet. One solution.",
    description:
      "Every elevator fleet is different. OPENlift is designed to integrate with many controller families and make it easier to monitor equipment, regardless of manufacturer.",
    note: "Compatibility depends on models and configurations",
    groundTitle: "Designed for the field",
    benefits: [
      "Multi-manufacturer interoperability",
      "Progressive controller integration",
      "Centralized monitoring"
    ],
    commonPlatform: "Shared platform",
    diagramLabel: "Manufacturers and controllers connected to one OPENlift platform"
  }
};

function splitHeading(value) {
  const index = value.indexOf(". ");
  if (index === -1) return [value, null];
  return [value.slice(0, index + 1), value.slice(index + 2)];
}

export default function Products() {
  const { t, i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const fallback = CONTENT[language] || CONTENT.fr;

  // Préservation des clés de traduction déjà utilisées par la section validée.
  const label = t("compatibilityHome.label", { defaultValue: fallback.label });
  const title = t("compatibilityHome.title", { defaultValue: fallback.title });
  const description = t("compatibilityHome.description", {
    defaultValue: fallback.description
  });
  const note = t("compatibilityHome.note", { defaultValue: fallback.note });

  const groundTitle = t("compatibilityHome.groundTitle", {
    defaultValue: fallback.groundTitle
  });
  const benefits = fallback.benefits.map((benefit, index) =>
    t(`compatibilityHome.benefits.${index}`, { defaultValue: benefit })
  );
  const commonPlatform = t("compatibilityHome.commonPlatform", {
    defaultValue: fallback.commonPlatform
  });
  const diagramLabel = t("compatibilityHome.diagramLabel", {
    defaultValue: fallback.diagramLabel
  });
  const [titleFirst, titleSecond] = splitHeading(title);

  return (
    <section className="compatibility-home" aria-labelledby="compatibility-home-title">
      <div className="compatibility-home__container">
        <div className="compatibility-home__layout">
          <div className="compatibility-home__editorial">
            <header className="compatibility-home__header">
              <p className="compatibility-home__eyebrow">{label}</p>
              <h2 id="compatibility-home-title" className="compatibility-home__title">
                {titleFirst}
                {titleSecond && (
                  <span className="compatibility-home__title-second">{titleSecond}</span>
                )}
              </h2>
              <p className="compatibility-home__description">{description}</p>
              <span className="compatibility-home__accent" aria-hidden="true" />
              <p className="compatibility-home__note">
                <span className="compatibility-home__note-dot" aria-hidden="true" />
                {note}
              </p>
            </header>

            <div className="compatibility-home__proof">
              <h3 className="compatibility-home__proof-title">{groundTitle}</h3>
              <ul className="compatibility-home__benefits">
                {benefits.map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="compatibility-home__diagram" aria-label={diagramLabel}>
            <ul className="compatibility-home__brands" aria-label={label}>
              {MANUFACTURERS.map((manufacturer) => (
                <li className="compatibility-home__brand" key={manufacturer}>
                  <span className="compatibility-home__brand-name">{manufacturer}</span>
                </li>
              ))}
            </ul>

            <div className="compatibility-home__connector" aria-hidden="true">
              <svg
                className="compatibility-home__arrow"
                viewBox="0 0 64 64"
                xmlns="http://www.w3.org/2000/svg"
                focusable="false"
              >
                <path d="M28 5H36V39H51L32 59L13 39H28V5Z" fill="currentColor" />
              </svg>
            </div>

            <div className="compatibility-home__platform">
              <strong className="compatibility-home__platform-name">OPENlift</strong>
              <span className="compatibility-home__platform-type">{commonPlatform}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
